/**
 * Profile — your own display name (every signed-in persona), plus, for an org's super_admin
 * only, the organization's profile (name, industry, website, phone, logo) and four opt-in
 * email-notification toggles (new requisition, new candidate, interview completed, score ready).
 *
 * Sign-in protection (MFA, preferred-email verification, password) stays on Security — this
 * page is identity/organization details, not auth.
 */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { NotificationPrefs } from "@talenthop/shared";
import { api, ApiError } from "../lib/api.js";
import { useAuth } from "../lib/AuthContext.js";
import { useMyOrg } from "./users/useMyOrg.js";
import { Alert, CheckBig, UploadCloud } from "../components/icons.js";
import { apiErrorText } from "./admin/adminUi.js";
import { BehavioralQuestionsCard } from "./BehavioralQuestionsCard.js";

/**
 * Theme 1 settings switch (UX audit X8 / top-15 #9). The admin Switch it replaces on Profile and
 * Security was a 1.3:1 pale track nobody could read as a control. This is the global .t1-switch
 * (app.css): visible off track, forest + lime when on, focus ring, written On/Off state, and a
 * real checkbox with role="switch" underneath. Exported for AccountSecurity.
 */
export function SettingSwitch({
  on,
  disabled,
  onToggle,
  label,
}: {
  on: boolean;
  disabled?: boolean;
  onToggle: () => void;
  /** accessible name — the row title it controls */
  label: string;
}): ReactNode {
  return (
    <label className="t1-switch">
      <input
        type="checkbox"
        role="switch"
        checked={on}
        aria-label={label}
        disabled={disabled}
        onChange={onToggle}
      />
      <span className="track" aria-hidden="true" />
      <span className="t1s-switch-state" aria-hidden="true">{on ? "On" : "Off"}</span>
    </label>
  );
}

/** "Zenith Talent Partners" → "ZT" for the logo placeholder (audit 24: an org, not a person). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "—";
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const NOTIFY_ROWS: { key: keyof NotificationPrefs; label: string; hint: string }[] = [
  { key: "requisitionCreated", label: "New requirement", hint: "Whenever anyone in your org creates a requisition." },
  { key: "candidateAdded", label: "New candidate", hint: "Once per upload — a batch of resumes is one email, not one per resume." },
  { key: "interviewCompleted", label: "Interview completed", hint: "When a candidate finishes their interview." },
  { key: "scoreReady", label: "Score ready", hint: "When a requisition's scored report is ready to review." },
];

export function Profile(): ReactNode {
  const { me, refreshMe } = useAuth();
  const { org, isLoading: orgLoading } = useMyOrg();
  const queryClient = useQueryClient();
  const isSuperAdmin = me?.role === "super_admin";

  // ── Your profile (everyone) ───────────────────────────────────────────────
  const [name, setName] = useState(me?.name ?? "");
  useEffect(() => {
    if (me?.name) setName(me.name);
  }, [me?.name]);
  const saveName = useMutation({
    mutationFn: () => api.patch<{ name: string }>("/me", { name: name.trim() }),
    onSuccess: () => {
      // AuthContext's `me` is plain state, not a react-query cache — refetch it directly so the
      // nav avatar/greeting pick up the new name immediately instead of only this form.
      void refreshMe();
    },
  });
  const nameDirty = name.trim().length > 0 && name.trim() !== (me?.name ?? "");

  // ── Organization (super_admin only) ───────────────────────────────────────
  const [orgName, setOrgName] = useState("");
  const [industry, setIndustry] = useState("");
  const [website, setWebsite] = useState("");
  const [phone, setPhone] = useState("");
  const [orgHydrated, setOrgHydrated] = useState(false);
  useEffect(() => {
    if (!org || orgHydrated) return;
    setOrgName(org.name);
    setIndustry(org.industry ?? "");
    setWebsite(org.website ?? "");
    setPhone(org.phone ?? "");
    setOrgHydrated(true);
  }, [org, orgHydrated]);

  const saveOrg = useMutation({
    mutationFn: () =>
      api.patch(`/orgs/${org?.id}`, {
        name: orgName.trim(),
        industry: industry.trim(),
        website: website.trim(),
        phone: phone.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["my-org"] });
    },
  });

  const uploadLogo = useMutation({
    mutationFn: async (file: File) =>
      api.post<{ ok: boolean; logoUrl: string | null; message?: string }>(`/orgs/${org?.id}/logo`, {
        filename: file.name,
        mime: file.type || "application/octet-stream",
        contentBase64: await fileToDataUrl(file),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["my-org"] });
    },
  });

  // ── Notifications (super_admin only) ──────────────────────────────────────
  const prefsQuery = useQuery({
    queryKey: ["notification-prefs", org?.id],
    queryFn: () => api.get<{ prefs: NotificationPrefs }>(`/orgs/${org?.id}/notification-prefs`),
    enabled: isSuperAdmin && !!org?.id,
    retry: 1,
  });
  const savePrefs = useMutation({
    mutationFn: (next: NotificationPrefs) => api.put<{ prefs: NotificationPrefs }>(`/orgs/${org?.id}/notification-prefs`, next),
    onSuccess: (res) => {
      queryClient.setQueryData(["notification-prefs", org?.id], { prefs: res.prefs });
    },
  });
  const prefs = prefsQuery.data?.prefs;
  const togglePref = (key: keyof NotificationPrefs): void => {
    if (!prefs) return;
    savePrefs.mutate({ ...prefs, [key]: !prefs[key] });
  };

  return (
    <section className="t1s-page t1s-narrow">
      <div className="page-h">
        <div>
          <h1>Profile</h1>
          <div className="sub">Your display name{isSuperAdmin ? ", your organization's details, interview settings and email alerts" : ""}</div>
        </div>
      </div>

          <div className="card">
            <div className="card-h">
              <h3>Your profile</h3>
            </div>
            <div className="card-b">
              <div className="field mb-2.5">
                <label htmlFor="profile-name">Name</label>
                <input
                  id="profile-name"
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={160}
                  placeholder="Your name"
                />
              </div>
              {saveName.isSuccess && !nameDirty && (
                <div className="banner info mb-3">
                  <CheckBig />
                  <span>Saved.</span>
                </div>
              )}
              {saveName.isError && (
                <div className="banner crit mb-3">
                  <Alert />
                  <span>{apiErrorText(saveName.error, "Couldn't save your name. Please retry.")}</span>
                </div>
              )}
              <button
                className="btn primary"
                disabled={!nameDirty || saveName.isPending}
                title={!nameDirty ? "Change your name to save it" : undefined}
                onClick={() => saveName.mutate()}
              >
                {saveName.isPending ? "Saving…" : "Save name"}
              </button>
            </div>
          </div>
          {isSuperAdmin && (
            <div className="card">
              <div className="card-h">
                <h3>Email notifications</h3>
                <span className="pill n">Only sent to you</span>
              </div>
              <div className="card-b">
                <p className="mut sm t1s-help">
                  Opt in to any of these — each fires an email to your own account address.
                  Toggling here applies immediately.
                </p>
                {(prefsQuery.isLoading || orgLoading) && <p className="mut sm">Loading…</p>}
                {!orgLoading && !org?.id && (
                  <div className="banner crit mb-3">
                    <Alert />
                    <span>Couldn't determine your organization — refresh the page to try again.</span>
                  </div>
                )}
                {prefsQuery.isError && (
                  <div className="banner crit mb-3">
                    <Alert />
                    <span className="flex-1">{apiErrorText(prefsQuery.error, "Couldn't load your notification settings.")}</span>
                    <button type="button" className="btn ghost sm" onClick={() => prefsQuery.refetch()}>
                      Retry
                    </button>
                  </div>
                )}
                {savePrefs.isError && (
                  <div className="banner crit mb-3">
                    <Alert />
                    <span>{apiErrorText(savePrefs.error, "Couldn't update that. Please retry.")}</span>
                  </div>
                )}
                {prefs && (
                  <div>
                    {NOTIFY_ROWS.map((row) => (
                      <div key={row.key} className="t1s-switch-row">
                        <div className="t1s-switch-text">
                          <div className="t1s-switch-title">{row.label}</div>
                          <div className="t1s-switch-hint">{row.hint}</div>
                        </div>
                        <SettingSwitch
                          label={row.label}
                          on={prefs[row.key]}
                          disabled={savePrefs.isPending}
                          onToggle={() => togglePref(row.key)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        {isSuperAdmin && org?.id && <BehavioralQuestionsCard orgId={org.id} />}
        {isSuperAdmin && (
          <div className="card">
            <div className="card-h">
              <h3>Organization</h3>
              <span className="pill t">Super admin</span>
            </div>
            <div className="card-b">
              <div className="flex items-center gap-3.5 mb-4">
                <div className="t1s-logo" aria-hidden="true">
                  {org?.logoUrl ? <img src={org.logoUrl} alt="" /> : initials(orgName || org?.name || "")}
                </div>
                <div>
                  <label className="btn ghost sm cursor-pointer">
                    <UploadCloud /> {uploadLogo.isPending ? "Uploading…" : "Upload logo"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      hidden
                      disabled={uploadLogo.isPending}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (f) uploadLogo.mutate(f);
                      }}
                    />
                  </label>
                  <div className="mut sm mt-1">PNG, JPEG, WEBP or SVG — up to 2 MB.</div>
                </div>
              </div>
              {uploadLogo.data && !uploadLogo.data.ok && (
                <div className="banner crit" style={{ marginBottom: 12 }}>
                  <Alert />
                  <span>{uploadLogo.data.message ?? "Couldn't save the logo."}</span>
                </div>
              )}
              {uploadLogo.isError && (
                <div className="banner crit" style={{ marginBottom: 12 }}>
                  <Alert />
                  <span>{apiErrorText(uploadLogo.error, "Couldn't upload the logo. Please retry.")}</span>
                </div>
              )}

              <div className="field">
                <label htmlFor="org-name">Organization name</label>
                <input id="org-name" className="input" value={orgName} onChange={(e) => setOrgName(e.target.value)} maxLength={200} />
              </div>
              <div className="t1s-fields-2">
                <div className="field">
                  <label htmlFor="org-industry">Industry</label>
                  <input id="org-industry" className="input" value={industry} onChange={(e) => setIndustry(e.target.value)} maxLength={120} placeholder="e.g. Staffing & recruiting" />
                </div>
                <div className="field">
                  <label htmlFor="org-phone">Phone</label>
                  <input id="org-phone" className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} placeholder="+91 98765 43210" />
                </div>
              </div>
              <div className="field">
                <label htmlFor="org-website">Website</label>
                <input id="org-website" className="input" type="url" value={website} onChange={(e) => setWebsite(e.target.value)} maxLength={300} placeholder="https://yourcompany.com" />
              </div>
              {saveOrg.isSuccess && (
                <div className="banner info" style={{ marginBottom: 12 }}>
                  <CheckBig />
                  <span>Saved.</span>
                </div>
              )}
              {saveOrg.isError && (
                <div className="banner crit" style={{ marginBottom: 12 }}>
                  <Alert />
                  <span>{apiErrorText(saveOrg.error, "Couldn't save the organization. Please retry.")}</span>
                </div>
              )}
              <button className="btn primary" disabled={saveOrg.isPending || !orgName.trim()} onClick={() => saveOrg.mutate()}>
                {saveOrg.isPending ? "Saving…" : "Save organization"}
              </button>
            </div>
          </div>
        )}
    </section>
  );
}
